import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import { usePastedFields } from '@/contexts/PastedFieldsContext';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import {
  computeRoomPasteOutcome,
  parseRoomTariffBlock,
} from '@/utils/benefits/hotel/excelPaste/parseRoomTariffBlock';
import ExcelPastePanel from './ExcelPastePanel';
import {
  buildLabelAndPeriodsRecap,
  FormPeriod,
  toChildBandsCount,
} from './pasteFormHelpers';
import {
  buildRoomAmountsRecap,
  buildRoomOptionsNotes,
  writePastedRoomTariff,
} from './roomPasteHelpers';
import {
  getCategoryTickedOptions,
  updateCategoryRoomOptions,
} from './updateCategoryRoomOptions';
import { useExcelPasteFormatters } from './useExcelPasteFormatters';

interface RoomTariffPasteProps {
  categoryIndex: number;
  tariffIndex: number;
  /** Pasting is only offered for room prices ("Total chambre"). */
  pricingType?: 'byPerson' | 'byRoom';
}

/**
 * Paste of a 28×2 room block into one tariff of a category: label, periods
 * and the 20 "Total chambre" amounts. Compositions then follow the prices: a
 * composition stays ticked when one tariff of the category prices it, the
 * others are unticked (updateCategoryRoomOptions keeps every tariff aligned).
 */
const RoomTariffPaste = ({
  categoryIndex,
  tariffIndex,
  pricingType,
}: RoomTariffPasteProps) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } =
    useFormContext<HotelFormDataWithTranslations>();
  const { markPasted } = usePastedFields();
  const childAges = useWatch({ control, name: 'childAges' });
  const { formatIssue, formatComposition } = useExcelPasteFormatters(childAges);

  const categoryPath = `hotelRoomCategories.${categoryIndex}` as const;

  const parse = useCallback(
    (text: string) => {
      const childBandsCount = toChildBandsCount(
        getValues('acceptsChildPricing') === 'true'
          ? (getValues('childAges')?.length ?? 0)
          : 0,
      );
      const category = getValues(categoryPath);
      const tickedOptions = getCategoryTickedOptions(category);
      const parsed = parseRoomTariffBlock(text, childBandsCount);
      if (!parsed.value) {
        return {
          childBandsCount,
          category,
          tickedOptions,
          parsed,
          computed: null,
        };
      }
      const computed = computeRoomPasteOutcome(parsed.value, {
        childBandsCount,
        roomOptions: tickedOptions,
        roomTypes: category.roomTypes ?? {},
        targetTariffIndex: tariffIndex,
        tariffs: (category.tarifs ?? []).map((tariff, index) => ({
          name:
            tariff.name ||
            t('hotels.roomCategory.tarifs.tarif_', { index: index + 1 }),
          periods: tariff.periods ?? [],
          pricePartitions: tariff.pricePartitions,
        })),
      });
      return { childBandsCount, category, tickedOptions, parsed, computed };
    },
    [categoryPath, getValues, t, tariffIndex],
  );

  const analyze = useCallback(
    (text: string) => {
      const { childBandsCount, category, tickedOptions, parsed, computed } =
        parse(text);
      if (!parsed.value || !computed) {
        return {
          errors: parsed.errors.map(formatIssue),
          warnings: [],
          notes: [],
          recap: [],
        };
      }

      const current = category.tarifs?.[tariffIndex];
      return {
        errors: [],
        warnings: computed.warnings.map(formatIssue),
        notes: buildRoomOptionsNotes(
          computed.outcome,
          childBandsCount,
          formatComposition,
          t,
        ),
        recap: [
          ...buildLabelAndPeriodsRecap(
            { name: current?.name, periods: current?.periods as FormPeriod[] },
            parsed.value,
            t,
          ),
          ...buildRoomAmountsRecap(
            current,
            tickedOptions,
            parsed.value,
            childBandsCount,
            formatComposition,
          ),
        ],
      };
    },
    [formatComposition, formatIssue, parse, t, tariffIndex],
  );

  const apply = useCallback(
    async (text: string) => {
      const { computed } = parse(text);
      if (!computed) return;
      const { pasted, roomOptions: nextOptions } = computed.outcome;

      await updateCategoryRoomOptions(
        { getValues, setValue },
        categoryIndex,
        nextOptions,
      );
      markPasted(
        writePastedRoomTariff(
          { getValues, setValue },
          `${categoryPath}.tarifs.${tariffIndex}`,
          pasted,
          nextOptions,
        ),
      );
    },
    [
      categoryIndex,
      categoryPath,
      getValues,
      markPasted,
      parse,
      setValue,
      tariffIndex,
    ],
  );

  return (
    <ExcelPastePanel
      shape="room"
      analyze={analyze}
      apply={apply}
      disabledReason={
        pricingType === 'byPerson'
          ? t('hotels.pasteFromExcel.byPersonDisabled')
          : undefined
      }
    />
  );
};

export default RoomTariffPaste;
